import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { apiRequest, formatBigNo, formatDate, formatFileSize, parseDataFromAPI, toast } from "@/Helpers/general";
import { Accordion, AccordionTab } from 'primereact/accordion';
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import { IL_ROWS_NO } from "@/Config/constants";
import InfiniteScroll from "react-infinite-scroll-component";
import { AppContext } from "@/Components/Context/AppContext";
import { getFormErrorMessage, isFormFieldValid, mapFieldsData, validateForm } from "@/Helpers/forms";
import { Panel } from "primereact/panel";
import { offersPaymentsFields } from "@/Config/forms/offers-payments";
import DownloadButton from "../DownloadButton";
import moment from "moment";
import TextInputTemplate from "@/Components/Templates/Forms/TextInputTemplate";
import { caseFormConfig, fieldsPlaceholders } from "@/Config/forms";
import TextareaInputTemplate from "@/Components/Templates/Forms/TextareaInputTemplate";
import DateInputTemplate from "@/Components/Templates/Forms/DateInputTemplate";
import { Button } from "primereact/button";
import DropdownInputTemplate from "@/Components/Templates/Forms/DropdownInputTemplate";
import NumberInputTemplate from "@/Components/Templates/Forms/NumberInputTemplate";
import CustomSelectTemplate from "@/Components/Templates/Forms/CustomSelectTemplate";

const OffersWrap = styled.div`
    .offers-list-wrap {
        border-right: 1px solid var(--gray-200);
        padding-right: 10px;

        .offer-item-wrap {
            padding: 10px 13px;
            opacity: 0.7;
            cursor: pointer;
            border-radius: 4px;

            &:hover, &.active {
                opacity: 1;
                background-color: var(--gray-200);
            }

            .offer-name, .offer-date {
                font-size: 13px;
            }

            .offer-name {
                font-weight: bold;
            }
        }
    }

    .offer-preview {
        .offer-header {
            width: 100%;
            font-size: 18px;

            i.pi {
                color: var(--primary-color);
                font-size: 22px;
            }

            .filename-preview {
                font-size: 16px;
            }
        }
    }

    .loader-wrap {
        position: relative;
        height: 60px;
    }

    .no-offers {
        padding: 1.5rem;
    }
`;

/**
 * Component which renders the offers tab
 */
const Offers = ({ caseId, triggerSave, disabled = false }) => {
    const { toastRef } = useContext(AppContext);

    const [offers, setOffers] = useState([]);
    const [selectedOfferId, setSelectedOfferId] = useState('');
    const [selectedOfferName, setSelectedOfferName] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreOffers, setHasMoreOffers] = useState(true);
    const [accordionErrors, setAccordionErrors] = useState({});
    const [formData, setFormData] = useState();
    const [formErrors, setFormErrors] = useState({});
    const [activeAccordionIndex, setActiveAccordionIndex] = useState(0);
    const [formLoading, setFormLoading] = useState(false);
    const [formValidationRules, setFormValidationRules] = useState([]);

    /**
     * Create an array with offers data
     */
    const makeOffersArray = (offer) => {
        let offersArr = [];

        for (let key in offer) {
            offersArr.push({
                key: key,
                value: offer[key]
            });
        }

        return offersArr;
    };

    /**
     * Update the state of the form data (when changing an input value)
     *
     * @param {string} name the name of the input
     * @param {string} value the new value of the input
     */
    const changeFormData = (name, value) => {
        let getFormData = { ...formData },
            allFields = [];

        offersPaymentsFields.forEach((config) => {
            allFields = allFields.concat(config.fields);
        });

        let fieldConfig = allFields.find(field => field.name == name);

        if (fieldConfig) {
            // check if this field is a dependency source
            if (fieldConfig.dependencySource && value != fieldConfig.dependencyMatchValue) {
                allFields.forEach((field) => {
                    // if dependent fields will be hidden, clear their values
                    if (field.dependency && field.dependency.type == 'visibility' && field.dependency.sourceField == name) {
                        changeFormData(field.name, '');
                    }
                });
            }

            if (fieldConfig.calculate) {
                // check if this fields is a date range and calculate the number of days selected
                if (fieldConfig.type == 'date_range_start' && fieldConfig.calculate?.type == 'days') {
                    if (Array.isArray(value) && value[0] && value[1]) {
                        let startDate = moment(value[0]),
                            endDate = moment(value[1]),
                            daysBetween,
                            totalDays = 0;

                        if (startDate.isValid() && endDate.isValid()) {
                            daysBetween = startDate.diff(endDate, 'days');
                        }

                        if (daysBetween && fieldConfig.calculate?.targetField) {
                            getFormData[fieldConfig.calculate?.targetField] = Math.abs(daysBetween);

                            totalDays += Math.abs(daysBetween);
                        }

                        // go thru each number of days field and calculate the total
                        allFields.forEach((item) => {
                            if (item.name != fieldConfig.calculate?.targetField
                                && item?.calculate?.group == fieldConfig?.dateRange?.key
                                && getFormData[item.name]) {
                                totalDays += parseInt(getFormData[item.name]);
                            }
                        });

                        getFormData[fieldConfig.calculate?.totalField] = totalDays;
                    }
                }

                // check if this is a number of days field and update the total amount of days field
                if (fieldConfig.calculate?.type == 'days' && fieldConfig.calculate?.group) {
                    // go thru each number of days field and update the total
                    let totalDaysNo = 0;

                    allFields.forEach((fieldItem) => {
                        if (fieldItem.name == fieldConfig.name) {
                            totalDaysNo += value;

                        } else {
                            if (fieldItem?.calculate?.group == fieldConfig?.calculate?.group
                                && getFormData[fieldItem.name]) {
                                totalDaysNo += parseInt(getFormData[fieldItem.name]);
                            }
                        }
                    });

                    getFormData[fieldConfig.calculate?.totalField] = totalDaysNo;
                }
            }

            // check if this field is a dropdown which must generate dropdown options for another field
            if (fieldConfig.dropdownOptionsDependFor) {
                if (fieldConfig?.dropdownOptionsAPI == 'gtaGroups') {
                    apiRequest('get', 'gtagroups', {
                        data: {
                            filters: {
                                name: value,
                                group_by: 'rate'
                            }
                        },
                        onSuccess: (response) => {
                            let copyDdlOptions = { ...dropdownsOptionsDependency },
                                newOptions = [];

                            if (response.result) {
                                response.result.forEach((item) => {
                                    newOptions.push({
                                        label: item.rate,
                                        value: item.rate
                                    });
                                });

                                copyDdlOptions[fieldConfig.dropdownOptionsDependFor] = newOptions;

                            } else {
                                copyDdlOptions[fieldConfig.dropdownOptionsDependFor] = [];
                            }

                            setDropdownsOptionsDependency(copyDdlOptions);
                        },
                        onFailure: () => { }
                    });
                }
            }
        }

        getFormData[name] = value;

        setFormData(getFormData);
    };

    const generateInputField = (tab, field) => {
        switch (field.type) {
            case 'text':
                return (
                    <TextInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                        labelClassName="one-line"
                        value={formData && formData[field.name] ? formData[field.name] : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        // category={tab.key}
                        disabled={disabled}
                    />
                );

            case 'textarea':
                return (
                    <TextareaInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                        value={formData && formData[field.name] ? formData[field.name] : ''}
                        bottomMargin={2}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                    // category={tab.key}
                    />
                );

            case 'date':
                return (
                    <DateInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.date}
                        labelClassName="one-line"
                        value={formData && formData[field.name] ? formData[field.name] : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        // category={tab.key}
                        disabled={disabled}
                    />
                );

            case 'date_range_start':
                return (<>
                    {(field.dateRange.index == 0
                        || (field.dateRange.index <= visibleDateRanges[field.dateRange.key])
                        || (formData && formData[tab.key][field.name])) && <DateInputTemplate
                            name={field.name}
                            label={field.label}
                            placeholder={field.custom_placeholder || fieldsPlaceholders.dateRange}
                            labelClassName="one-line"
                            value={formData && formData[field.name] ? formData[field.name] : ''}
                            bottomMargin={3}
                            onChangeFunction={changeFormData}
                            isValid={isFormFieldValid(formErrors, field.name)}
                            errorMessage={getFormErrorMessage(formErrors, field.name)}
                            // category={tab.key}
                            disabled={disabled}
                            selectionMode="range"
                        />}
                    {field.dateRange.index == visibleDateRanges[field.dateRange.key] + 1 &&
                        <div className="mb-3 relative">
                            <label htmlFor={field.name + '-add-btn'} className="w-full block mb-2 pr-4 one-line">&nbsp;</label>
                            <Button className="grey-nav-btn grey-nav-btn-hover" label="Add date range" onClick={() => addDateRange(field)} icon="pi pi-plus" />
                        </div>}
                </>);
                break;

            case 'boolean':
            case 'dropdown':
                return (
                    <DropdownInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.boolean}
                        labelClassName="one-line"
                        value={formData && formData[field.name] ? formData[field.name] : ''}
                        options={field.type == 'dropdown'
                            ?
                            (field.dropdownOptionsAPI && dropdownsOptionsApi && dropdownsOptionsApi[field.dropdownOptionsAPI]
                                ? dropdownsOptionsApi[field.dropdownOptionsAPI]
                                : (field.dropdownOptions && dropdownsOptions && dropdownsOptions[field.name]
                                    ? dropdownsOptions[field.name]
                                    : []))
                            : booleanOptions}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        // category={tab.key}
                        disabled={disabled || (field.name == 'cho_id' && initialFormData && initialFormData['cho_id'] != '')}
                        filter={field?.dropdownOptionsAPI == 'gtaGroups'}
                    />
                );

            case 'integer':
                return (
                    <NumberInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                        labelClassName="one-line"
                        value={formData && formData[field.name] ? formData[field.name] : null}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        // category={tab.key}
                        disabled={disabled}
                    />
                );

            case 'float':
                return (
                    <NumberInputTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                        labelClassName="one-line"
                        value={formData && formData[field.name] ? formData[field.name] : null}
                        mode={'decimal'}
                        bottomMargin={3}
                        format
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        // category={tab.key}
                        disabled={disabled}
                    />
                );

            case 'custom-select':
                return (
                    <CustomSelectTemplate
                        name={field.name}
                        label={field.label}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.boolean}
                        labelClassName="one-line"
                        value={formData && formData[field.name] ? formData[field.name] : ''}
                        options={field.dropdownOptionsDependOn && dropdownsOptionsDependency && dropdownsOptionsDependency[field.name]
                            ? dropdownsOptionsDependency[field.name]
                            : field.customOptions}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        // category={tab.key}
                        disabled={disabled}
                    />
                );

            default:
                break;
        }
    };

    /**
     * Accordion header
     *
     * @param {object} category object with category data
     */
    const accordionHeader = (category) => {
        // let errorsNo = accordionErrors && accordionErrors[currentTabName][category.key] ? accordionErrors[currentTabName][category.key] : 0;

        return (
            <div className="flex justify-content-between w-full">
                <span className="p-accordion-header-text">{category.name}</span>
                {/* {errorsNo > 0 && <span className="p-error">{accordionErrors[currentTabName][category.key]} errors</span>} */}
            </div>
        );
    };

    /**
     * Generate the panel header
     *
     * @param {object} e event object
     * @param {object} currentOffer object with selected offer data
     */
    const panelHeader = (e, currentOffer) => {
        return (
            <div className="flex justify-content-between align-items-center">
                <span>{selectedOfferName}</span>

                {currentOffer.attachment && <span className="flex align-items-center filename-preview">
                    <i className="pi pi-file-pdf mr-1"></i> {currentOffer.attachment?.name} <DownloadButton doc={currentOffer.attachment} margin={3} size="small" />
                </span>}
            </div>
        );
    };

    /**
     * Generate a preview of an offer
     */
    const generateOfferPreview = () => {
        let currentOffer = offers?.find((offer) => offer.id == selectedOfferId);

        if (currentOffer) {
            let offersArray = makeOffersArray(currentOffer);

            return (<>
                <div className="general-form relative offers-payments">
                    <div className="flex justify-content-between align-items-center offer-header">
                        <span className="font-semibold">{selectedOfferName}</span>

                        {currentOffer.attachment && <span className="flex align-items-center filename-preview">
                            <i className="pi pi-file-pdf mr-1"></i> {currentOffer.attachment?.name} <DownloadButton doc={currentOffer.attachment} margin={3} size="small" />
                        </span>}
                    </div>

                    <Accordion activeIndex={activeAccordionIndex} onTabChange={(e) => setActiveAccordionIndex(e.index)} className="my-4">
                        {offersPaymentsFields.map((category) => {
                            return (
                                <AccordionTab headerTemplate={() => accordionHeader(category)} key={category.key}>
                                    <div className="formgrid grid my-3">
                                        <div className="col-12">
                                            <div className="grid">
                                                {category.fields.map((field) => {
                                                    // if (canShowField(tab, field)) {
                                                    return (
                                                        <div className="field col-12 md:col-6 lg:col-6" key={field.name}>
                                                            {generateInputField(category, field)}
                                                        </div>
                                                    );
                                                    // }
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </AccordionTab>
                            );
                        })}
                    </Accordion>

                    {formLoading && <ContentLoadingTemplate />}
                </div>
            </>);
        }
    };

    /**
     * Get offers from the API
     */
    const loadOffers = async () => {
        let requestParams = {
            page: currentPage,
            limit: IL_ROWS_NO
        };

        apiRequest('get', 'case/' + caseId + '/offers', {
            data: requestParams,
            onSuccess: (response) => {
                if (response.result && response.result.length > 0) {
                    let validationRules = [],
                        allFields = [];

                    offersPaymentsFields.forEach((config) => {
                        allFields = allFields.concat(config.fields);
                    });

                    if (currentPage == 1) {
                        setOffers(response.result);

                    } else {
                        setOffers([...offers, ...response.result]);
                    }

                    if (currentPage == 1) {
                        allFields.forEach((field) => {
                            // set validation rules
                            if (field.validation && Object.keys(field.validation).length > 0) {
                                validationRules.push({
                                    field: field.name,
                                    rules: field.validation
                                });
                            }
                        });

                        setSelectedOfferId(response.result[0].id);
                        setSelectedOfferName('Offer ' + response.result.length);
                        setFormData(parseDataFromAPI(response.result[0], 'offers_payments'));
                        setFormValidationRules(validationRules);
                    }

                    if (currentPage == response.pagination.totalPages) {
                        setHasMoreOffers(false);
                    }

                    setCurrentPage(currentPage + 1);

                } else {
                    setHasMoreOffers(false);
                }
            },
            onFailure: () => {
                setHasMoreOffers(false);
            }
        });
    };

    /**
     * Save the current offer
     */
    const submitForm = async () => {
        setFormLoading(true);

        let validation = validateForm(formValidationRules, formData, setFormErrors);

        if (validation && formData && formData.id) {
            apiRequest('post', 'case/' + caseId + '/offer/' + formData.id, {
                data: {...formData},
                onSuccess: (response) => {
                    setFormLoading(false);
                    // setCurrentPage(1);
                    // loadOffers();

                    toast(toastRef, 'success', 'Offer data has been updated.');
                },
                onFailure: (error) => {
                    setFormLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            setFormErrors(newFormErrors);
                            toast(toastRef, 'error', 'Error saving offer data.');

                        } else {
                            toast(toastRef, 'error', 'Error saving offer data.');
                        }

                    } else if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                    } else {
                        toast(toastRef, 'error', 'Error saving offer data.');
                    }
                }
            });

        } else {
            setFormLoading(false);
        }
    };

    /**
     * Get the next page
     */
    const loadNextPage = () => {
        if (hasMoreOffers) {
            loadOffers();
        }
    };

    /**
     * Reload offers
     */
    useEffect(() => {
        if (currentPage == 1) {
            loadOffers();
        }

    }, [currentPage]);

    useEffect(() => {
        // jump to the first available error (if we have any)
        if (formErrors) {
            let getFirstErrorLabel = document.querySelector('label[for="' + Object.keys(formErrors)[0] + '"]');

            if (getFirstErrorLabel) {
                getFirstErrorLabel.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

    }, [formErrors]);

    /**
     * Trigger save from case modal
     */
    useEffect(() => {
        if (triggerSave) {
            submitForm();
        }

    }, [triggerSave]);

    return (<>
        <OffersWrap>
            <div className="grid">
                <div className="col-3">
                    <div className="offers-list-wrap">
                        <InfiniteScroll
                            height="60vh"
                            dataLength={offers.length}
                            next={loadNextPage}
                            hasMore={hasMoreOffers}
                            loader={<div className="loader-wrap"><ContentLoadingTemplate /></div>}
                            endMessage={offers.length == 0 && <div className="no-offers">No offers found.</div>}
                        >
                            {offers.map((offer, index) => {
                                return (
                                    <div key={offer.id} className={'offer-item-wrap' + (selectedOfferId == offer.id ? ' active' : '')} onClick={() => {
                                        setSelectedOfferId(offer.id);
                                        setSelectedOfferName('Offer ' + (offers.length - index));
                                        setFormData(parseDataFromAPI(offer, 'offers_payments'));
                                    }}>
                                        <div className="flex justify-content-between">
                                            <span className="offer-name">Offer {offers.length - index}</span>
                                            <span className="offer-date">{formatDate(offer.created_at)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </InfiniteScroll>
                    </div>
                </div>

                <div className="col-9">
                    <div className="offer-preview">
                        {generateOfferPreview()}
                    </div>
                </div>
            </div>
        </OffersWrap>
    </>);
};

export default Offers;
